import { useState, useEffect, useCallback, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  getFirestore,
  collection,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  getDocs,
  getCountFromServer,
  doc,
  getDoc
} from 'firebase/firestore'
import { getAuth } from 'firebase/auth'
import toast from 'react-hot-toast'
import { app } from '../firebase'
import './KnowledgeBrowsePage.css'
import DeviceSelector from '../components/DecviceSelector'
import KnowledgeDetailModal from '../components/KnowledgeDetailModal'
import ImageGallery from '../components/ImageGallery'
import Leaderboard from '../components/contribute/Leaderboard'
import { useAuth } from '../contexts/AuthContext'
import { useCompany } from '../contexts/CompanyContext'
import { useAuthState } from '../hooks/useAuthState'
import ImageHeader from '../components/ImageHeader'
import UserService from '../services/UserService'
import {
  exportKnowledgeToExcel,
  loadAllKnowledgeForExport
} from '../utils/excelExport'
import {
  getDifficultyColor,
  getDifficultyLabel,
  getCategoryIcon,
  getCategoryLabel
} from '../utils/knowledgeHelpers'

function KnowledgeBrowsePage () {
  const { itemId } = useParams()
  const { user } = useAuth()
  const { features, headerImageUrl } = useCompany()
  const { hasAdminAccess, userRole } = useAuthState()
  const navigate = useNavigate()

  const [knowledgeItems, setKnowledgeItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [lastDoc, setLastDoc] = useState(null)
  const [hasMore, setHasMore] = useState(true)
  const [selectedItem, setSelectedItem] = useState(null)
  const [currentUser, setCurrentUser] = useState(null)
  const [totalCount, setTotalCount] = useState(null)
  const [loadingCount, setLoadingCount] = useState(false)
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768)
  const [exporting, setExporting] = useState(false)
  const [deviceDisplayNameMap, setDeviceDisplayNameMap] = useState(new Map()) // Mapping: displayName -> customDisplayName

  // Load devices for display name mapping
  useEffect(() => {
    const loadDevices = async () => {
      if (!user || !user.uid) {
        return
      }

      try {
        const companyId = await UserService.getUserCompanyId(user.uid)
        const db = getFirestore(app)
        const devicesRef = collection(db, 'companies', companyId, 'devices')
        const snapshot = await getDocs(devicesRef)
        
        const map = new Map()
        snapshot.docs.forEach(doc => {
          const deviceData = doc.data()
          if (deviceData.displayName) {
            // Verwende customDisplayName falls vorhanden, sonst displayName
            map.set(deviceData.displayName, deviceData.customDisplayName || deviceData.displayName)
          }
        })
        
        setDeviceDisplayNameMap(map)
      } catch (error) {
        console.error('Error loading devices for display name mapping:', error)
      }
    }

    loadDevices()
  }, [user])

  // Auth State
  useEffect(() => {
    const auth = getAuth(app)
    const unsubscribe = auth.onAuthStateChanged(user => {
      setCurrentUser(user)
    })
    return () => unsubscribe()
  }, [])

  // Load user favorites
  useEffect(() => {
    const loadFavorites = async () => {
      if (!user || !user.uid) {
        console.log('⏳ User not ready yet, skipping favorites load')
        return
      }

      // Add small delay to ensure auth token is fully initialized
      await new Promise(resolve => setTimeout(resolve, 100))

      try {
        console.log('📥 Loading favorites for user:', user.uid)
        const db = getFirestore(app)
        const favoritesRef = collection(db, 'users', user.uid, 'favorites')
        const snapshot = await getDocs(favoritesRef)
        const favIds = new Set(snapshot.docs.map(doc => doc.id))
        console.log(`✅ Loaded ${favIds.size} favorites`)
        setFavorites(favIds)
      } catch (error) {
        console.error('❌ Error loading favorites:', error)
        console.error('Error code:', error.code)
        console.error('Error message:', error.message)
        // Don't show alert on permission-denied during initial load
        if (error.code !== 'permission-denied') {
          console.warn('Unexpected error loading favorites')
        }
      }
    }

    loadFavorites()
  }, [user])

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768)
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Filter States
  const [filters, setFilters] = useState({
    deviceName: '',
    category: 'all',
    difficulty: 'all',
    searchTerm: '',
    sortBy: 'newest',
    showFavoritesOnly: false
  })
  const [favorites, setFavorites] = useState(new Set())

  const ITEMS_PER_PAGE = isMobile ? 10 : 20

  // Debounced Search
  const [debouncedSearch, setDebouncedSearch] = useState('')

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(filters.searchTerm)
    }, 500)
    return () => clearTimeout(timer)
  }, [filters.searchTerm])

  // Item aus URL laden
  useEffect(() => {
    if (itemId) {
      loadItemById(itemId)
    } else {
      setSelectedItem(null)
    }
  }, [itemId, deviceDisplayNameMap])

  // Funktion zum Laden eines einzelnen Items
  const loadItemById = async id => {
    try {
      if (!user) {
        console.warn('User not authenticated')
        navigate('/browse', { replace: true })
        return
      }

      const db = getFirestore(app)
      // Get user's companyId
      const companyId = await UserService.getUserCompanyId(user.uid)

      const docRef = doc(db, 'companies', companyId, 'knowledge', id)
      const docSnap = await getDoc(docRef)

      if (docSnap.exists() && docSnap.data().status === 'approved') {
        const itemData = docSnap.data()
        // ⭐ NEU: Füge customDisplayName hinzu, falls vorhanden
        const displayName = itemData.deviceName
        const customDisplayName = deviceDisplayNameMap.get(displayName) || displayName
        
        setSelectedItem({
          id: docSnap.id,
          ...itemData,
          deviceDisplayName: customDisplayName // Neues Feld für Anzeige
        })
      } else {
        console.error('Item nicht gefunden oder nicht freigegeben')
        // Optional: Zurück zur Browse-Seite ohne Item
        navigate('/browse', { replace: true })
      }
    } catch (error) {
      console.error('Error loading item:', error)
      navigate('/browse', { replace: true })
    }
  }

  // Item öffnen - URL ändern
  const handleOpenItem = item => {
    navigate(`/browse/${item.id}`)
  }

  // Modal schließen - URL zurücksetzen
  const handleCloseModal = () => {
    navigate('/browse')
  }

  // Sortier-Funktion (muss vor useMemo definiert sein)
  const applySorting = useCallback((items, sortBy) => {
    const sorted = [...items]
    switch (sortBy) {
      case 'newest':
        return sorted.sort((a, b) => {
          const dateA = a.moderatedAt?.toDate?.() || new Date(0)
          const dateB = b.moderatedAt?.toDate?.() || new Date(0)
          return dateB - dateA
        })
      case 'oldest':
        return sorted.sort((a, b) => {
          const dateA = a.moderatedAt?.toDate?.() || new Date(0)
          const dateB = b.moderatedAt?.toDate?.() || new Date(0)
          return dateA - dateB
        })
      case 'mostLiked':
        return sorted.sort(
          (a, b) => (b.stats?.likes || 0) - (a.stats?.likes || 0)
        )
      case 'alphabetical':
        return sorted.sort((a, b) =>
          (a.problemTitle || '').localeCompare(b.problemTitle || '')
        )
      default:
        return sorted
    }
  }, [])

  // ⭐ NEU: Aktualisiere Items, wenn Device-Mapping geladen wird
  useEffect(() => {
    if (deviceDisplayNameMap.size > 0 && knowledgeItems.length > 0) {
      // Prüfe ob ein Update nötig ist (nur wenn deviceDisplayName fehlt oder falsch ist)
      const needsUpdate = knowledgeItems.some(item => {
        const displayName = item.deviceName
        const expectedDisplayName = deviceDisplayNameMap.get(displayName) || displayName
        return item.deviceDisplayName !== expectedDisplayName
      })
      
      if (needsUpdate) {
        // Aktualisiere deviceDisplayName für alle vorhandenen Items
        setKnowledgeItems(prevItems => {
          return prevItems.map(item => {
            const displayName = item.deviceName
            const customDisplayName = deviceDisplayNameMap.get(displayName) || displayName
            return {
              ...item,
              deviceDisplayName: customDisplayName
            }
          })
        })
      }
    }
  }, [deviceDisplayNameMap, knowledgeItems.length]) // Nur wenn Mapping sich ändert oder Items hinzugefügt werden

  // Reset bei Filter-Änderung (außer Sortierung)
  useEffect(() => {
    setLastDoc(null)
    setHasMore(true)
    setKnowledgeItems([])
    setTotalCount(null)
    loadKnowledge(false)
  }, [
    filters.deviceName,
    filters.category,
    filters.difficulty,
    filters.showFavoritesOnly,
    debouncedSearch
  ])

  // Sortierte Items (wird automatisch neu berechnet bei Sortier-Änderung)
  const sortedKnowledgeItems = useMemo(() => {
    console.log('🔄 Sorting items with:', filters.sortBy)
    return applySorting(knowledgeItems, filters.sortBy)
  }, [knowledgeItems, filters.sortBy])

  // Helper: Query Builder
  const buildBaseQuery = useCallback(
    async companyId => {
      const db = getFirestore(app)
      let q = query(
        collection(db, 'companies', companyId, 'knowledge'),
        where('status', '==', 'approved'), // Nur genehmigte Beiträge anzeigen
        orderBy('moderatedAt', 'desc')
      )

      // Firestore-Filter anwenden
      if (filters.deviceName) {
        q = query(q, where('deviceName', '==', filters.deviceName))
      }
      if (filters.category !== 'all') {
        q = query(q, where('category', '==', filters.category))
      }
      if (filters.difficulty !== 'all') {
        q = query(q, where('difficulty', '==', filters.difficulty))
      }

      return q
    },
    [filters]
  )

  // Total Count laden (nur ohne Suche)
  const loadTotalCount = useCallback(async () => {
    if (debouncedSearch || !user) {
      return
    }

    setLoadingCount(true)
    try {
      const companyId = await UserService.getUserCompanyId(user.uid)
      const baseQuery = await buildBaseQuery(companyId)
      const snapshot = await getCountFromServer(baseQuery)
      setTotalCount(snapshot.data().count)
    } catch (error) {
      console.error('Error loading count:', error)
      setTotalCount(null)
    } finally {
      setLoadingCount(false)
    }
  }, [buildBaseQuery, debouncedSearch, user])

  const loadKnowledge = useCallback(
    async (loadMore = false) => {
      if (!user) {
        console.warn('User not authenticated')
        return
      }

      setLoading(true)

      try {
        const companyId = await UserService.getUserCompanyId(user.uid)
        const db = getFirestore(app)
        let q = await buildBaseQuery(companyId)

        // Pagination
        if (loadMore && lastDoc) {
          q = query(q, startAfter(lastDoc))
        }

        q = query(q, limit(ITEMS_PER_PAGE))

        const snapshot = await getDocs(q)
        let items = snapshot.docs.map(doc => {
          const itemData = doc.data()
          // ⭐ NEU: Füge customDisplayName hinzu, falls vorhanden
          const displayName = itemData.deviceName
          const customDisplayName = deviceDisplayNameMap.get(displayName) || displayName
          
          return {
            id: doc.id,
            ...itemData,
            deviceDisplayName: customDisplayName // Neues Feld für Anzeige
          }
        })

        // Client-side Search-Filter nur auf aktuellen Batch
        let filteredItems = items
        if (debouncedSearch) {
          const searchLower = debouncedSearch.toLowerCase()
          filteredItems = items.filter(
            item =>
              item.problemTitle?.toLowerCase().includes(searchLower) ||
              item.solution?.toLowerCase().includes(searchLower) ||
              item.problemDescription?.toLowerCase().includes(searchLower) ||
              item.errorCode?.toLowerCase().includes(searchLower)
          )
        }

        // Favoriten-Filter
        if (filters.showFavoritesOnly) {
          filteredItems = filteredItems.filter(item => favorites.has(item.id))
        }

        // Sortierung wird über useMemo in sortedKnowledgeItems angewendet

        // State Updates
        if (loadMore) {
          setKnowledgeItems(prev => [...prev, ...filteredItems])
        } else {
          setKnowledgeItems(filteredItems)
          if (!debouncedSearch) {
            loadTotalCount()
          }
        }

        const lastVisible = snapshot.docs[snapshot.docs.length - 1]
        setLastDoc(lastVisible)

        const hasFullPage = snapshot.docs.length === ITEMS_PER_PAGE
        setHasMore(
          hasFullPage && (!debouncedSearch || filteredItems.length > 0)
        )
      } catch (error) {
        console.error('Error loading knowledge:', error)
        setKnowledgeItems([])
        setHasMore(false)
      } finally {
        setLoading(false)
      }
    },
    [
      filters,
      debouncedSearch,
      lastDoc,
      buildBaseQuery,
      loadTotalCount,
      ITEMS_PER_PAGE,
      user,
      deviceDisplayNameMap
    ]
  )

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }))
  }

  const resetFilters = () => {
    setFilters({
      deviceName: '',
      category: 'all',
      difficulty: 'all',
      searchTerm: '',
      sortBy: 'newest',
      showFavoritesOnly: false
    })
  }

  // Favoriten Toggle
  const toggleFavorite = async itemId => {
    if (!user || !user.uid) {
      toast.error('Sie müssen angemeldet sein, um Favoriten zu speichern.')
      return
    }

    // Optimistic update
    const wasAlreadyFavorite = favorites.has(itemId)
    if (wasAlreadyFavorite) {
      setFavorites(prev => {
        const newSet = new Set(prev)
        newSet.delete(itemId)
        return newSet
      })
    } else {
      setFavorites(prev => new Set([...prev, itemId]))
    }

    try {
      const db = getFirestore(app)
      const favoriteRef = doc(db, 'users', user.uid, 'favorites', itemId)

      if (wasAlreadyFavorite) {
        // Favorit entfernen
        console.log('🗑️ Removing favorite:', itemId)
        await import('firebase/firestore').then(({ deleteDoc }) =>
          deleteDoc(favoriteRef)
        )
        console.log('✅ Favorite removed')
      } else {
        // Favorit hinzufügen
        console.log('⭐ Adding favorite:', itemId)
        await import('firebase/firestore').then(({ setDoc }) =>
          setDoc(favoriteRef, {
            addedAt: new Date(),
            knowledgeId: itemId
          })
        )
        console.log('✅ Favorite added')
      }
    } catch (error) {
      console.error('❌ Error toggling favorite:', error)
      console.error('Error code:', error.code)
      console.error('Error message:', error.message)

      // Rollback optimistic update
      if (wasAlreadyFavorite) {
        setFavorites(prev => new Set([...prev, itemId]))
      } else {
        setFavorites(prev => {
          const newSet = new Set(prev)
          newSet.delete(itemId)
          return newSet
        })
      }

      // Show user-friendly error
      if (error.code === 'permission-denied') {
        toast.error(
          'Keine Berechtigung. Bitte laden Sie die Seite neu und versuchen Sie es erneut.'
        )
      } else {
        toast.error('Fehler beim Speichern des Favoriten: ' + error.message)
      }
    }
  }

  const handleExportToExcel = async () => {
    if (!user) {
      toast.error('Sie müssen angemeldet sein, um Daten zu exportieren.')
      return
    }

    setExporting(true)
    try {
      const companyId = await UserService.getUserCompanyId(user.uid)

      // Lade alle gefilterten Daten
      const allItems = await loadAllKnowledgeForExport(
        buildBaseQuery,
        companyId,
        debouncedSearch
      )

      if (allItems.length === 0) {
        toast.error('Keine Daten zum Exportieren vorhanden.')
        setExporting(false)
        return
      }

      // Bestimme Gerätenamen für Dateiname
      const uniqueDevices = [
        ...new Set(allItems.map(item => item.deviceName).filter(Boolean))
      ].sort()

      let devicePart = ''
      if (uniqueDevices.length === 1) {
        // Nur ein Gerät - verwende Gerätenamen
        devicePart = uniqueDevices[0].replace(/[^a-zA-Z0-9-_]/g, '-') + '_'
      } else if (uniqueDevices.length > 1 && uniqueDevices.length <= 3) {
        // 2-3 Geräte - liste sie alle auf
        devicePart =
          uniqueDevices.map(d => d.replace(/[^a-zA-Z0-9-_]/g, '-')).join('-') +
          '_'
      } else if (uniqueDevices.length > 3) {
        // Mehr als 3 Geräte - verwende "Mehrere-Geraete"
        devicePart = 'Mehrere-Geraete_'
      }

      const filename = `Techniker-Wissen_${devicePart}${
        new Date().toISOString().split('T')[0]
      }.xlsx`

      // Exportiere als Excel mit einem Sheet pro Gerät
      exportKnowledgeToExcel(allItems, {
        filename,
        groupByDevice: true
      })

      console.log(
        `✓ ${allItems.length} Einträge exportiert (${uniqueDevices.length} Gerät(e))`
      )
    } catch (error) {
      console.error('Fehler beim Excel-Export:', error)
      toast.error('Fehler beim Exportieren der Daten: ' + error.message)
    } finally {
      setExporting(false)
    }
  }

  const getStatusText = () => {
    if (loading && knowledgeItems.length === 0) {
      return 'Lade Einträge...'
    }

    const itemText = knowledgeItems.length === 1 ? 'Eintrag' : 'Einträge'

    if (debouncedSearch) {
      return `${knowledgeItems.length} ${itemText} gefunden (gefiltert nach "${debouncedSearch}")`
    } else if (totalCount !== null && !loadingCount) {
      return `${knowledgeItems.length} von ${totalCount} ${itemText} geladen`
    } else {
      return `${knowledgeItems.length} ${itemText} geladen`
    }
  }

  return (
    <div className='knowledge-browse-page'>
      {features?.imageHeader && (
        <ImageHeader
          imageUrl={headerImageUrl}
          companyId={user?.userData?.companyId}
        />
      )}
      <div className='browse-header'>
        <h1>Techniker-Wissen durchsuchen</h1>
        <p>{getStatusText()}</p>
      </div>

      {/* Filter Bar */}
      <div className='page-layout'>
        <div className='main-content'>
          <div className='filter-bar'>
            {/* Zeile 1: Hauptfilter */}
            <div className='filter-row filter-row-main'>
              <div className='filter-group search-group'>
                <div className='search-input-wrapper'>
                  <svg
                    className='search-icon'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='2'
                  >
                    <circle cx='11' cy='11' r='8' />
                    <path d='M21 21l-4.35-4.35' />
                  </svg>
                  <input
                    type='text'
                    placeholder='Suche nach Titel, Fehlercode, Beschreibung...'
                    value={filters.searchTerm}
                    onChange={e =>
                      handleFilterChange('searchTerm', e.target.value)
                    }
                    className='search-input'
                  />
                  {filters.searchTerm && (
                    <button
                      className='clear-search'
                      onClick={() => handleFilterChange('searchTerm', '')}
                      title='Suche löschen'
                    >
                      <svg
                        viewBox='0 0 24 24'
                        fill='none'
                        stroke='currentColor'
                        strokeWidth='2'
                      >
                        <line x1='18' y1='6' x2='6' y2='18' />
                        <line x1='6' y1='6' x2='18' y2='18' />
                      </svg>
                    </button>
                  )}
                </div>
              </div>

              <div className='filter-group'>
                <DeviceSelector
                  value={filters.deviceName}
                  onChange={value => handleFilterChange('deviceName', value)}
                  showLabel={false}
                  compact={true}
                />
              </div>

              <div className='filter-group'>
                <select
                  value={filters.category}
                  onChange={e => handleFilterChange('category', e.target.value)}
                  className='filter-select'
                >
                  <option value='all'>Alle Kategorien</option>
                  <option value='error_code'>Fehlercodes</option>
                  <option value='troubleshooting'>Troubleshooting</option>
                  <option value='maintenance'>Wartung</option>
                  <option value='workaround'>Workaround</option>
                  <option value='general_knowledge'>Allgemeines Wissen</option>
                </select>
              </div>

              <div className='filter-group'>
                <select
                  value={filters.difficulty}
                  onChange={e =>
                    handleFilterChange('difficulty', e.target.value)
                  }
                  className='filter-select'
                >
                  <option value='all'>Alle Schwierigkeiten</option>
                  <option value='easy'>Einfach</option>
                  <option value='medium'>Mittel</option>
                  <option value='hard'>Schwierig</option>
                </select>
              </div>
            </div>

            {/* Zeile 2: Sortierung und Aktionen */}
            <div className='filter-row filter-row-actions'>
              <div className='filter-group-compact'>
                <label className='filter-label'>Sortierung:</label>
                <select
                  value={filters.sortBy}
                  onChange={e => handleFilterChange('sortBy', e.target.value)}
                  className='filter-select-compact'
                >
                  <option value='newest'>Neueste zuerst</option>
                  <option value='oldest'>Älteste zuerst</option>
                  <option value='mostLiked'>Meiste Likes</option>
                  <option value='alphabetical'>Alphabetisch</option>
                </select>
              </div>

              <div className='filter-group-compact'>
                <label className='favorites-toggle'>
                  <input
                    type='checkbox'
                    checked={filters.showFavoritesOnly}
                    onChange={e =>
                      handleFilterChange('showFavoritesOnly', e.target.checked)
                    }
                  />
                  <svg
                    viewBox='0 0 24 24'
                    fill={filters.showFavoritesOnly ? '#fbbf24' : 'none'}
                    stroke='currentColor'
                    strokeWidth='2'
                    style={{
                      width: '18px',
                      height: '18px',
                      marginRight: '0.35rem'
                    }}
                  >
                    <path d='M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z' />
                  </svg>
                  Nur Favoriten
                </label>
              </div>

              <div className='filter-actions'>
                {(filters.deviceName ||
                  filters.category !== 'all' ||
                  filters.difficulty !== 'all' ||
                  filters.searchTerm) && (
                  <button className='btn-reset-compact' onClick={resetFilters}>
                    <svg
                      viewBox='0 0 24 24'
                      fill='none'
                      stroke='currentColor'
                      strokeWidth='2'
                    >
                      <path d='M3 12a9 9 0 019-9 9.75 9.75 0 016.74 2.74L21 8' />
                      <path d='M21 3v5h-5' />
                    </svg>
                    Zurücksetzen
                  </button>
                )}

                {hasAdminAccess && (
                  <button
                    className='btn-export-compact'
                    onClick={handleExportToExcel}
                    disabled={exporting || loading}
                    title='Als Excel exportieren'
                  >
                    {exporting ? (
                      <>
                        <div className='spinner-small'></div>
                        Exportiere...
                      </>
                    ) : (
                      <>
                        <svg
                          viewBox='0 0 24 24'
                          fill='none'
                          stroke='currentColor'
                          strokeWidth='2'
                        >
                          <path d='M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4' />
                          <polyline points='7 10 12 15 17 10' />
                          <line x1='12' y1='15' x2='12' y2='3' />
                        </svg>
                        Excel Export
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Knowledge Grid */}
      {loading && knowledgeItems.length === 0 ? (
        <div className='loading-state'>
          <div className='spinner-large'></div>
          <p>Lade Wissen...</p>
        </div>
      ) : knowledgeItems.length === 0 ? (
        <div className='empty-state'>
          <svg
            viewBox='0 0 24 24'
            fill='none'
            stroke='currentColor'
            strokeWidth='1.5'
          >
            <circle cx='11' cy='11' r='8' />
            <path d='M21 21l-4.35-4.35' />
          </svg>
          <h3>Keine Einträge gefunden</h3>
          <p>Versuche andere Filter oder Suchbegriffe</p>
        </div>
      ) : (
        <>
          <div className='knowledge-grid'>
            {sortedKnowledgeItems.map(item => (
              <div
                onClick={() => handleOpenItem(item)}
                key={item.id}
                className='knowledge-card'
                onTouchStart={e => e.currentTarget.classList.add('touching')}
                onTouchEnd={e => e.currentTarget.classList.remove('touching')}
              >
                <button
                  className='favorite-star'
                  onClick={e => {
                    e.stopPropagation()
                    toggleFavorite(item.id)
                  }}
                  title={
                    favorites.has(item.id)
                      ? 'Aus Favoriten entfernen'
                      : 'Zu Favoriten hinzufügen'
                  }
                >
                  <svg
                    viewBox='0 0 24 24'
                    fill={favorites.has(item.id) ? '#fbbf24' : 'none'}
                    stroke='currentColor'
                    strokeWidth='2'
                  >
                    <path d='M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z' />
                  </svg>
                </button>
                <div>
                  <div className='card-header'>
                    <span className='category-icon'>
                      {getCategoryIcon(item.category)}
                    </span>
                    <span className='category-label'>
                      {getCategoryLabel(item.category)}
                    </span>
                    <span className='device-badge'>{item.deviceDisplayName || item.deviceName}</span>
                  </div>

                  {item.errorCode && (
                    <div className='error-code-badge'>
                      <svg
                        viewBox='0 0 24 24'
                        fill='none'
                        stroke='currentColor'
                        strokeWidth='2'
                      >
                        <path d='M10 2v2m4-2v2M6 6h12M6 10h12M6 14h12M6 18h12' />
                      </svg>
                      {item.errorCode}
                    </div>
                  )}

                  <h3 className='card-title'>{item.problemTitle}</h3>

                  {item.problemDescription && (
                    <p className='card-description'>
                      {item.problemDescription.substring(0, 120)}
                      {item.problemDescription.length > 120 && '...'}
                    </p>
                  )}

                  <div className='card-meta'>
                    <span
                      className='difficulty-badge'
                      style={{
                        background: getDifficultyColor(item.difficulty)
                      }}
                    >
                      {getDifficultyLabel(item.difficulty)}
                    </span>
                    {item.estimatedTime && (
                      <span className='time-badge'>
                        <svg
                          viewBox='0 0 24 24'
                          fill='none'
                          stroke='currentColor'
                          strokeWidth='2'
                        >
                          <circle cx='12' cy='12' r='10' />
                          <polyline points='12 6 12 12 16 14' />
                        </svg>
                        {item.estimatedTime}
                      </span>
                    )}
                  </div>

                  {item.images && item.images.length > 0 && (
                    <div className='card-images'>
                      <ImageGallery
                        images={item.images}
                        compact={true}
                        maxPreview={2}
                        clickHandler={false}
                        marginTop={false}
                      />
                    </div>
                  )}

                  <div className='card-footer'>
                    <span className='submitter'>Von: {item.submittedBy}</span>
                    <div className='card-stats'>
                      {item.stats?.likes > 0 && (
                        <span className='stat-badge'>
                          <svg
                            viewBox='0 0 24 24'
                            fill='currentColor'
                            stroke='none'
                          >
                            <path d='M14 9V5a3 3 0 00-3-3l-4 9v11h11.28a2 2 0 002-1.7l1.38-9a2 2 0 00-2-2.3zM7 22H4a2 2 0 01-2-2v-7a2 2 0 012-2h3' />
                          </svg>
                          {item.stats.likes}
                        </span>
                      )}
                      {item.stats?.commentCount > 0 && (
                        <span className='stat-badge'>
                          <svg
                            viewBox='0 0 24 24'
                            fill='none'
                            stroke='currentColor'
                            strokeWidth='2'
                          >
                            <path d='M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z' />
                          </svg>
                          {item.stats.commentCount}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {hasMore && (
            <div className='load-more-section'>
              <button
                className='btn-load-more'
                onClick={() => loadKnowledge(true)}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <div className='spinner-small'></div>
                    Lädt...
                  </>
                ) : (
                  'Mehr laden'
                )}
              </button>
              {debouncedSearch && (
                <p
                  style={{
                    marginTop: '10px',
                    fontSize: '14px',
                    color: '#666',
                    textAlign: 'center'
                  }}
                >
                  Hinweis: Bei aktiver Suche werden Ergebnisse gefiltert
                </p>
              )}
            </div>
          )}
        </>
      )}
      <aside className='sidebar'>
        <Leaderboard currentUserEmail={currentUser?.email} />
      </aside>

      {selectedItem && (
        <KnowledgeDetailModal
          item={selectedItem}
          onClose={handleCloseModal}
          currentUser={currentUser}
          userRole={userRole}
          getCategoryIcon={getCategoryIcon}
          getCategoryLabel={getCategoryLabel}
          getDifficultyColor={getDifficultyColor}
          getDifficultyLabel={getDifficultyLabel}
        />
      )}
    </div>
  )
}

export default KnowledgeBrowsePage
